import get from 'lodash/get';
import sortBy from 'lodash/sortBy';
import { translate } from '../localization';
import { Data } from '../types/Dashboard';
import { MedicalContentType } from '../types/Medical';
import {
  ActionLevel,
  BannerContentType,
  BannerFrameType,
  BannerFramesListType,
  ContentsDataType,
  SectionsType,
  SocialContent,
  GeneratedSocialContent,
  ContentOptions,
  GeneratedEmailModuleContentDataType,
  GeneratedSocialContentDataType,
  CoreMessageContent,
  CoreMessageContentType,
} from '../types/Content';
import {
  BannerSuperScriptsType,
  BannerFrameSuperScriptType,
  ContentBannerSuperScriptType,
  EmailModulesSuperScriptsType,
  ContentEmailModulesSuperScriptType,
  SocialSuperScriptsType,
  ContentSocialSuperScriptType,
  ContentCoreMessageSuperScriptType,
} from '../types/SuperScripts';
import {
  APIRoleType,
  ListUserType,
  UserListType,
  UsersTransformedType,
} from '../types/Users';
import { WfTaskResponseData } from '../types/WfData';
import { DSPGeneratedContent } from '../components/molecules/DSPRenderer/DSPRenderer.types';
import { ClaimsSequenceType } from '../store/markettingContentGeneration/stateSegments/claimsSequenceStateSegment';

/** Constants for custom component prefixes */
export const headingWithLinesPrefix = '@headingWithLines';
export const subHeadingPrefix = '@subHeading';
export const spacer = '@spacer';

/* eslint-disable */

export function getTransformedBannerData(
  bannerRawStructure: any,
  lang: string
): ContentsDataType[] {
  const bannerTranslation = translate('transformers_option', {}, lang);
  const frameTranslation = translate('transformers_frame', {}, lang);

  const contents = bannerRawStructure.generatedContent.map(
    (el: any, i: number) => {
      const { frames } = el;
      let bodyContent = '';
      frames.forEach((frame: any, j: number) => {
        const { body } = frame;
        bodyContent += `__${frameTranslation} ${j + 1}:__ ${body}`;
        bodyContent += `\n\n`;
      });

      return {
        title: `${bannerTranslation} ${i + 1}`,
        sections: [
          {
            name: '',
            actionLevel: ActionLevel.SECTION,
            body: bodyContent,
          },
        ],
      };
    }
  );

  return contents;
}

export function getTransformedBannerContent(
  generatedContent: BannerFramesListType[]
): BannerContentType[] {
  const bannerTranslation = translate('transformers_option');
  const frameTranslation = translate('transformers_frame');
  const callToActionLabel = translate('common_call_to_action');

  // Handle multi-language responses - use the first language for display
  // but this could be enhanced to return content for all languages
  const contentToProcess =
    generatedContent.length > 0 ? generatedContent[0] : null;

  if (!contentToProcess) {
    return [];
  }

  const { contentOptions } = contentToProcess;

  return contentOptions.map((content: ContentOptions, i: number) => {
    const { frames, optionId, call_to_action } = content;
    const bodyContent = frames.map((frame: BannerFrameType, j: number) => {
      const { body, id } = frame;
      return {
        id,
        body: `__${frameTranslation} ${j + 1}:__ ${body}`,
      };
    });

    bodyContent.push({
      id: 'cta',
      body: `__${callToActionLabel.trim()}:__ ${call_to_action}`,
    });

    return {
      title: `${bannerTranslation} ${i + 1}`,
      frames: [...bodyContent],
      isLoading: false,
      optionId,
    };
  });
}

// New function to handle multi-language banner content for export
export function getTransformedBannerContentMultiLanguage(
  generatedContent: BannerFramesListType[]
): { [language: string]: BannerContentType[] } {
  const bannerTranslation = translate('transformers_option');
  const frameTranslation = translate('transformers_frame');
  const callToActionLabel = translate('common_call_to_action');

  const result: { [language: string]: BannerContentType[] } = {};

  generatedContent.forEach(contentItem => {
    const language =
      (contentItem as any).language ||
      (contentItem as any).langCode ||
      'default';
    const { contentOptions } = contentItem;

    result[language] = contentOptions.map(
      (content: ContentOptions, i: number) => {
        const { frames, optionId, call_to_action } = content;
        const bodyContent = frames.map((frame: BannerFrameType, j: number) => {
          const { body, id } = frame;
          return {
            id,
            body: `__${frameTranslation} ${j + 1}:__ ${body}`,
          };
        });

        bodyContent.push({
          id: 'cta',
          body: `__${callToActionLabel.trim()}:__ ${call_to_action}`,
        });

        return {
          title: `${bannerTranslation} ${i + 1}`,
          frames: [...bodyContent],
          isLoading: false,
          optionId,
        };
      }
    );
  });

  return result;
}

export function getTransformedBannerSuperScripts(
  contentData: BannerFramesListType[],
  claimSequences: ClaimsSequenceType
): ContentBannerSuperScriptType[] {
  // Handle multi-language responses - use the first language for display
  const contentToProcess = contentData.length > 0 ? contentData[0] : null;

  if (!contentToProcess || !contentToProcess.superScripts) {
    return [];
  }

  const { superScripts = [], contentReferences = [] } = contentToProcess;

  // Create a map of reference IDs to their types (CLAIM or QUOTE)
  const referenceTypeMap = new Map<string, string>();
  contentReferences.forEach((ref: any) => {
    if (ref.id && ref.type) {
      referenceTypeMap.set(ref.id, ref.type);
    }
  });

  return superScripts.map((superScript: BannerSuperScriptsType, i: number) => {
    const { frames, optionId } = superScript;
    const bodyContent = frames.map(
      (frame: BannerFrameSuperScriptType, j: number) => {
        const { superScriptReferences, id } = frame;
        return {
          id,
          body: superScriptReferences.body.map((claim: string) => {
            const isQuote = referenceTypeMap.get(claim) === 'QUOTE';
            return {
              label: claimSequences[claim],
              value: claim,
              color: isQuote ? 'blue' : undefined,
            };
          }),
        };
      }
    );

    return {
      frames: [...bodyContent],
      isLoading: false,
      optionId,
    };
  });
}

export function getTransformedEmailModulesSuperScripts(
  superScripts: EmailModulesSuperScriptsType[] = [],
  claimSequences: ClaimsSequenceType,
  contentReferences: any[] = []
): ContentEmailModulesSuperScriptType[] {
  // Create a map of reference IDs to their types (CLAIM or QUOTE)
  const referenceTypeMap = new Map<string, string>();
  contentReferences.forEach((ref: any) => {
    if (ref.id && ref.type) {
      referenceTypeMap.set(ref.id, ref.type);
    }
  });

  return superScripts?.length
    ? superScripts.map(
        (superScript: EmailModulesSuperScriptsType, i: number) => {
          const { superScriptReferences, optionId } = superScript;
          // Explicitly asserting the type for indexing
          const references = superScriptReferences as Record<string, string[]>;
          const superScriptObj: Record<
            string,
            { label: string; value: string; color?: string }[]
          > = {};
          Object.keys(references).forEach((superScriptKey: string) => {
            superScriptObj[superScriptKey] = (
              references[superScriptKey] || []
            ).map((claim: string) => {
              const isQuote = referenceTypeMap.get(claim) === 'QUOTE';
              return {
                label: claimSequences[claim], // Ensure claimSequences[claim] exists
                value: claim,
                color: isQuote ? 'blue' : undefined,
              };
            });
          });
          return {
            ...superScriptObj,
            isLoading: false,
            optionId,
          };
        }
      )
    : [];
}

export function getTransformedCoreMessageSuperScripts(
  generatedContent: any,
  claimSequences: ClaimsSequenceType
): ContentCoreMessageSuperScriptType[] {
  // Handle multi-language responses - use the first language for display
  const contentToProcess =
    generatedContent.length > 0 ? generatedContent[0] : null;

  if (!contentToProcess) {
    return [];
  }

  const superScripts = get(contentToProcess, 'superScripts', []);
  const contentReferences = get(contentToProcess, 'contentReferences', []);

  // Create a map of reference IDs to their types (CLAIM or QUOTE)
  const referenceTypeMap = new Map<string, string>();
  contentReferences.forEach((ref: any) => {
    if (ref.id && ref.type) {
      referenceTypeMap.set(ref.id, ref.type);
    }
  });

  const componentMap: ContentCoreMessageSuperScriptType['components'] = {};

  superScripts.forEach(
    (superScript: { id: any; superScriptReferences: any }) => {
      const { id, superScriptReferences } = superScript;

      Object.keys(superScriptReferences).forEach(superScriptReference => {
        if (superScriptReferences[superScriptReference]) {
          superScriptReferences[superScriptReference].forEach(
            (item: string | number) => {
              if (!componentMap[id]) {
                componentMap[id] = [];
              }
              if (claimSequences[item]) {
                const isQuote =
                  referenceTypeMap.get(item.toString()) === 'QUOTE';
                componentMap[id].push({
                  label: claimSequences[item],
                  value: item.toString(),
                  color: isQuote ? 'blue' : undefined,
                });
              }
            }
          );
        }
      });
    }
  );

  return [{ isLoading: false, optionId: '', components: componentMap }];
}

export function getTransformedSocialSuperScripts(
  contentData: GeneratedSocialContentDataType[],
  claimSequences: ClaimsSequenceType
): ContentSocialSuperScriptType[] {
  // Handle multi-language responses - use the first language for display
  const contentToProcess = contentData.length > 0 ? contentData[0] : null;

  if (!contentToProcess || !contentToProcess.superScripts) {
    return [];
  }

  const { superScripts = [], contentReferences = [] } = contentToProcess;

  // Create a map of reference IDs to their types (CLAIM or QUOTE)
  const referenceTypeMap = new Map<string, string>();
  contentReferences.forEach((ref: any) => {
    if (ref.id && ref.type) {
      referenceTypeMap.set(ref.id, ref.type);
    }
  });

  return superScripts?.length
    ? superScripts.map((superScript: SocialSuperScriptsType, i: number) => {
        const { shortForm, longForm, optionId } = superScript;
        // Explicitly asserting the type for indexing

        const { superScriptReferences: shortFormSuperScriptReferences } =
          shortForm;
        const shortFormReferences = shortFormSuperScriptReferences as Record<
          string,
          string[]
        >;

        const { superScriptReferences: longFormSuperScriptReferences } =
          longForm;
        const longFormReferences = longFormSuperScriptReferences as Record<
          string,
          string[]
        >;

        const superScriptObj: Record<
          string,
          Record<string, { label: string; value: string; color?: string }[]>
        > = {};

        const fillData = (
          formKey: string,
          sectionKey: string,
          formReference: Record<string, string[]>
        ) => {
          superScriptObj[formKey] = { [sectionKey]: [] };

          superScriptObj[formKey][sectionKey] = formReference[sectionKey].map(
            (claim: string) => {
              const isQuote = referenceTypeMap.get(claim) === 'QUOTE';
              return {
                label: claimSequences[claim],
                value: claim,
                color: isQuote ? 'blue' : undefined,
              };
            }
          );
        };

        // shortForm headline
        fillData('shortForm', 'headline', shortFormReferences);

        // longForm headline
        fillData('shortForm', 'body', shortFormReferences);

        // shortForm headline
        fillData('longForm', 'headline', longFormReferences);

        // longForm headline
        fillData('longForm', 'body', longFormReferences);

        return {
          ...superScriptObj,
          isLoading: false,
          optionId,
        } as ContentSocialSuperScriptType;
      })
    : [];
}

export const getTransformedCannedParameters = (data: Data) => {
  /* eslint-disable */
  const formattedData: any = { brands: [] };

  data.brands.forEach(brand => {
    const { id: brandId } = brand;

    formattedData.brands.push({ ...brand, value: brand.id });
    fillOptionsRelativeToBrand(
      brandId,
      formattedData,
      data.targetAudiences,
      'targetAudiences'
    );
    fillOptionsRelativeToBrand(brandId, formattedData, data.tones, 'tones');
  });

  fillOptionsRelativeToBrandAndTargetAudience(
    formattedData,
    data.personas,
    'personas'
  );
  fillOptionsRelativeToBrandAndTargetAudience(
    formattedData,
    data.contentTypes,
    'contentTypes'
  );

  fillOptionsRelativeToBrandAndTargetAudience(
    formattedData,
    data.topics,
    'topics'
  );
  return formattedData;
};

const fillOptionsRelativeToBrand = (
  brandId: string,
  target: any,
  iterator: Array<any>,
  key: string
) => {
  iterator.forEach(item => {
    const { id: itemId, label: itemLabel, brands = [] } = item;

    if (brands.includes(brandId)) {
      if (!target[brandId]) {
        target[brandId] = {};
      }

      if (!target[brandId][key]) {
        target[brandId][key] = [];
      }
      target[brandId][key].push({
        id: itemId,
        label: itemLabel,
        value: itemId,
      });
    }
  });
};

const fillOptionsRelativeToBrandAndTargetAudience = (
  target: any,
  iterator: Array<any>,
  key: string
) => {
  const disabledContentTypeOptions = ['2', '3'];
  /* eslint-disable */
  iterator.forEach((item: any) => {
    const { id, label, brandTargetAudienceMapping = [] } = item;

    brandTargetAudienceMapping.forEach((mapping: any) => {
      const { brandId, targetAudienceId } = mapping;

      if (!target[brandId]) {
        target[brandId] = {};
      }

      if (!target[brandId][targetAudienceId]) {
        target[brandId][targetAudienceId] = {};
      }

      if (!target[brandId][targetAudienceId][key]) {
        target[brandId][targetAudienceId][key] = [];
      }

      if (key === 'contentType') {
        if (!disabledContentTypeOptions.includes(id)) {
          target[brandId][targetAudienceId][key].push({
            id,
            label,
            value: id,
          });
        }
      } else {
        target[brandId][targetAudienceId][key].push({
          id,
          label,
          value: id,
        });
      }
    });
  });
};

/* eslint-disable */
export function getTransformedMedicalData(summary: any): MedicalContentType[] {
  const {
    generatedContent: { sections },
  } = summary;
  const headline = sections.find(
    (section: SectionsType) => section.name === 'title'
  );
  const source = sections.find(
    (section: SectionsType) => section.name === 'citation'
  );

  const contents = [
    {
      title: translate('medical_content_headline_options'),
      sections: [
        {
          name: '',

          body: headline.body,
        },
      ],
    },
    {
      title: translate('medical_content_main_content'),
      sections: sections.filter(
        (section: SectionsType) =>
          section.name !== 'title' && section.name !== 'citation'
      ),
    },
    {
      title: translate('medical_content_source_attributes'),
      sections: [
        {
          name: '',
          body: source.body,
        },
      ],
    },
  ];
  return contents;
}

export function getTransformedEmailModuleData(
  generatedContent: GeneratedEmailModuleContentDataType[]
): GeneratedEmailModuleContentDataType[] {
  return generatedContent?.map(
    ({
      id,
      parentId,
      name,
      versionSequence,
      score,
      attributes,
      contentOptions,
      imagesRecommend,
      superScripts,
      langCode = '',
      langLabel = '',
    }: GeneratedEmailModuleContentDataType) => ({
      id,
      parentId,
      name,
      versionSequence,
      score,
      attributes,
      contentOptions,
      imagesRecommend,
      superScripts,
      langCode,
      langLabel,
    })
  );
}

export function getTransformedDSPModuleData(data: any): DSPGeneratedContent[] {
  return data as DSPGeneratedContent[];
}

export const transformUserDetails = (user: ListUserType) => {
  const { id, email, firstName, lastName, clientId, roles } = user;
  return {
    id,
    email,
    clientId,
    firstName,
    lastName,
    fullName: `${firstName || ''} ${lastName || ''}`,
    role: get(roles, '[0].roleCode', ''),
    roles: get(roles, '[0]', {}) as APIRoleType,
    isRoleBeingUpdated: false,
  };
};

export const getTransformedUsersData = ({
  users = [],
}: UserListType): UsersTransformedType[] => {
  return users.map(transformUserDetails);
};

export const getTransformedCoreMessageData = (
  generatedContent: any
): CoreMessageContent => {
  // Handle multi-language responses - use the first language for display
  const contentToProcess =
    generatedContent.length > 0 ? generatedContent[0] : null;

  if (!contentToProcess) {
    return {} as CoreMessageContent;
  }

  const generatedModuleContent = get(
    contentToProcess,
    'generatedModuleContent'
  );

  const dataMapList = [
    {
      code: 'HEADING',
      contentPath: 'generatedComponentData.header',
      dataKey: 'header',
    },
    {
      code: 'SUBHEADING',
      contentPath: 'generatedComponentData.subheader',
      dataKey: 'subheader',
    },

    {
      code: 'MESSAGE',
      contentPath: 'generatedComponentData.message',
      dataKey: 'message',
    },
    {
      code: 'CALL_TO_ACTION',
      contentPath: 'generatedComponentData.callToAction',
      dataKey: 'callToAction',
    },
  ];

  const coreMessageContent: Array<CoreMessageContentType> = [];

  const generatedModuleContentMap = generatedModuleContent.reduce(
    (
      acc: Record<string, {}[]>,
      item: {
        id: string;
        name: string;
        code: string;
        sequence: number;
        generatedComponentData: {
          header: string;
        };
      }
    ) => {
      if (!acc[item.code]) {
        acc[item.code] = [];
      }
      acc[item.code].push(item);

      return acc;
    },
    {}
  );

  dataMapList.forEach(dataMapItem => {
    const { code, contentPath } = dataMapItem;

    const mappedItems = generatedModuleContentMap[code] || [];

    mappedItems.forEach(
      (mappedItem: { id: any; code: any; name: any; sequence: any }) => {
        coreMessageContent.push({
          id: mappedItem.id,
          code: mappedItem.code,
          name: mappedItem.name,
          sequence: mappedItem.sequence,
          content: get(mappedItem, `${contentPath}`),
        });
      }
    );
  });

  return sortBy(coreMessageContent, 'sequence');
};

export function getTransformedSocialData(
  generatedContent: any
): SocialContent[] {
  const optionTitle = translate('transformers_option');
  const shortFormCopy = translate('transformers_social_short_form');
  const longFormCopy = translate('transformers_social_long_form');
  const socialHeadlineCopy = translate('transformers_social_headline');
  const socialBodyCopy = translate('transformers_social_body');
  const socialHastagsCopy = translate('transformers_social_hastags');

  // Handle multi-language responses - use the first language for display
  const contentToProcess =
    generatedContent.length > 0 ? generatedContent[0] : null;

  if (!contentToProcess) {
    return [];
  }

  const { contentOptions } = contentToProcess;
  const contents = contentOptions.map(
    (section: GeneratedSocialContent, index: number) => {
      const { optionId, longForm, shortForm } = section;
      return {
        title: `${optionTitle} ${index + 1}`,
        optionId: optionId,
        isLoading: false,
        shortForm: {
          name: 'shortform',
          title: shortFormCopy,
          isLoading: false,
          headline: {
            name: 'headline',
            title: socialHeadlineCopy,
            text: shortForm.headline,
          },
          body: {
            name: 'body',
            title: socialBodyCopy,
            text: shortForm.body,
          },
          hashtags: {
            name: 'hashtag',
            title: socialHastagsCopy,
            text: shortForm.hashtags
              .map(str => (str.startsWith('#') ? str : `#${str}`))
              .join(', '),
          },
        },
        longForm: {
          name: 'longform',
          title: longFormCopy,
          isLoading: false,
          headline: {
            name: 'headline',
            title: socialHeadlineCopy,
            text: longForm.headline,
          },
          body: {
            name: 'body',
            title: socialBodyCopy,
            text: longForm.body,
          },
          hashtags: {
            name: 'hashtag',
            title: socialHastagsCopy,
            text: longForm.hashtags
              .map(str => (str.startsWith('#') ? str : `#${str}`))
              .join(', '),
          },
        },
      };
    }
  );

  return contents;
}

export const getTransformedPromptData = (data: WfTaskResponseData) => {
  const { brand, indication, audience, channel, platform, ...rest } = data;
  const isBrandAvailable = !!brand;
  const isAudienceAvailable = !!audience;
  const isChannelAvailable = !!channel;
  return {
    brand: isBrandAvailable ? brand || '' : '',
    indication: isBrandAvailable ? indication || '' : '',
    audience: isBrandAvailable ? audience || '' : '',
    channel: isBrandAvailable && isAudienceAvailable ? channel || '' : '',
    platform:
      isBrandAvailable &&
      isAudienceAvailable &&
      isChannelAvailable &&
      channel.toLowerCase() === 'social'
        ? platform || ''
        : '',
    ...rest,
  };
};
